/**
 * @module app.um
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { LookupService } from 'app/cd/services/lookup-service';
import { AuthService } from 'app/um/services/auth-service';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference } from 'app/shared/models/essette-types';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';

@autoinject()
export class AddFlaggedDiagnoses extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable({ changeHandler: 'reload' })
    protected authFlagId: string | null = null;

    protected selectedDiagnoses: CD.Diagnosis[] = [];

    protected diagnosisSearchQuery: string | null = null;   // the value of the autocomplete text box
    protected diagnosisSearchResults: CD.Diagnosis[] = [];  // array of search results from API for autocomplete
    protected currentDiagnoses: CD.Diagnosis[] = [];        // array of current diagnoses from API

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        private readonly lookupService: LookupService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload()
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAuthFlagDiagnoses();
    }

    /** Called when a user clicks on an auto-complete search result */
    protected addDiagnosis(diagnosis: CD.Diagnosis): void {
        this.selectedDiagnoses = pushCopy(this.selectedDiagnoses, diagnosis);
        this.diagnosisSearchQuery = '';
        this.diagnosisSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected removeSelectedDiagnosis(diagnosis: CD.Diagnosis): void {
        this.selectedDiagnoses = spliceCopy(this.selectedDiagnoses, this.selectedDiagnoses.indexOf(diagnosis, 0), 1);
        if(this.selectedDiagnoses.length === 0) this.$autocomplete.clear();
    }

    protected filterDiagnoses = (diagnosis: CD.Diagnosis): boolean => {
        if(this.selectedDiagnoses.some(diag => diagnosis.id === diag.id)) return false;
        if(!this.currentDiagnoses && !diagnosis) { return false; }
        return !this.currentDiagnoses.some(diag => diagnosis.id === diag.id);
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return await this.addSelectedDiagnoses();
    }

    public reset(): void {
        this.selectedDiagnoses.splice(0);
        this.diagnosisSearchQuery = '';
        this.diagnosisSearchResults.splice(0);
        this.$autocomplete.clear();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAuthFlagDiagnoses(): Promise<void> {
        if(!this.authFlagId) return;

        try {
            this.state.beginLoad();
            const response = await this.service.loadDiagnosesByAuthFlag(this.authFlagId, { all: true });
            this.currentDiagnoses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete diagnosis search box */
    @apiCall
    protected async searchDiagnoses(value: string): Promise<void> {
        if(!value) {
            if(this.diagnosisSearchResults) this.diagnosisSearchResults.splice(0);
            return;
        }

        try {
            this.state.beginLoad();
            const response = await this.lookupService.searchDiagnoses(value, "", { top: 30 });
            this.diagnosisSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async addSelectedDiagnoses(): Promise<boolean> {
        if(!this.authFlagId) return false;
        const authFlagToInsert: UM.AuthFlag = {
            id: this.authFlagId,
            diagnoses: this.selectedDiagnoses.map(d => getReference(d)!)
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.service.insertAuthFlagDetails(authFlagToInsert);
            isSuccessful = response.isSuccessful;

            return isSuccessful;
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.added('um:auth.diagnosis.$', isSuccessful, this.selectedDiagnoses.length);
            this.state.endSave();
        }
    }
}
